/**
 * POLISH CATALOG SERVICE
 *
 * Reads from the `polishCatalog` collection (server-maintained by triggers
 * in functions/src/polishCatalogTriggers.ts) plus the per-collector items
 * needed to render the polish detail page's photo gallery.
 */

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Polish } from '../types/polish';
import { polishSlug } from '../utils/polishSlug';

export async function getPolishBySlug(slug: string): Promise<Polish | null> {
  const snap = await getDoc(doc(db, 'polishCatalog', slug));
  if (!snap.exists()) return null;
  return { ...(snap.data() as Polish), slug: snap.id };
}

export async function getPolishesByBrand(brandSlug: string): Promise<Polish[]> {
  const q = query(
    collection(db, 'polishCatalog'),
    where('brandSlug', '==', brandSlug)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...(d.data() as Polish), slug: d.id }));
}

export interface ContributorPhoto {
  url: string;
  caption?: string;
  type?: 'swatch' | 'nail-art' | 'macro' | 'bottle' | 'other';
  /** Optional per-photo context — see `Photo.skinTone`/`Photo.lighting`. */
  skinTone?: string;
  lighting?: string;
}

export interface PolishContribution {
  userId: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  photos: ContributorPhoto[];
}

const DMCA_HIDDEN = new Set(['hidden', 'removed', 'flagged']);

function extractPhotos(item: Record<string, any>): ContributorPhoto[] {
  const out: ContributorPhoto[] = [];
  if (Array.isArray(item.photos)) {
    for (const p of item.photos) {
      if (typeof p === 'string') {
        out.push({ url: p });
      } else if (p?.url && !DMCA_HIDDEN.has(p?.dmcaStatus)) {
        out.push({
          url: p.url,
          caption: p.caption,
          // Carry per-photo context through to the community gallery
          // so viewers see e.g. "medium warm · natural daylight" under
          // the thumbnail. Absent when uploader didn't fill them in.
          ...(typeof p.skinTone === 'string' && p.skinTone
            ? { skinTone: p.skinTone }
            : {}),
          ...(typeof p.lighting === 'string' && p.lighting
            ? { lighting: p.lighting }
            : {}),
        });
      }
    }
  }
  const legacy: Array<[string, ContributorPhoto['type']]> = [
    ['swatchPhotos', 'swatch'],
    ['inUsePhotos', 'nail-art'],
    ['macroPhotos', 'macro'],
    ['bottlePhotos', 'bottle'],
  ];
  for (const [field, type] of legacy) {
    const arr = item[field];
    if (Array.isArray(arr)) {
      for (const url of arr) {
        if (typeof url === 'string') out.push({ url, type });
      }
    }
  }
  return out;
}

/**
 * Aggregates photos AND user tags across every public collector who owns
 * this polish. Capped at 200 contributing items to bound Firestore reads
 * on viral polishes. Returns both contributions (for the photo gallery)
 * AND a union of tags across contributing items (for the detail-page
 * tag chip strip).
 */
export async function getPolishContributions(
  slug: string,
  maxItems = 200
): Promise<{ contributions: PolishContribution[]; tagsUnion: string[] }> {
  const itemsSnap = await getDocs(
    query(
      collectionGroup(db, 'items'),
      where('slug', '==', slug),
      limit(maxItems)
    )
  );

  // Group items by user. Brand items (parent path = brands/...) are skipped
  // here — official product photos are handled separately via the catalog's
  // verifiedBrandId/officialProductId fields.
  const itemsByUser = new Map<string, any[]>();
  // Collect every tag across every non-brand, non-deleted item — the
  // detail page renders the union so a tag added on any collector's
  // item shows up on the community view. Privacy filter still applies
  // via the profile pass below.
  const tagCollector = new Map<string, Set<string>>();
  for (const docSnap of itemsSnap.docs) {
    const item = docSnap.data();
    // Skip user-deleted and ban-hidden items. `hiddenByBan` is cascaded
    // onto every item under a banned user by onModerationStateChange so
    // their contributions drop out of the polish detail page immediately.
    // Also skip owner-private items — they should never appear on any
    // shared polish page, only on the owner's own profile.
    if (item.isDeleted || item.hiddenByBan === true || item.isPrivate === true) continue;
    const grandparent = docSnap.ref.parent.parent;
    if (grandparent?.parent?.id !== 'users') continue;
    const userId = grandparent.id;
    const arr = itemsByUser.get(userId) ?? [];
    arr.push(item);
    itemsByUser.set(userId, arr);
    const tags = Array.isArray(item.tags) ? (item.tags as string[]) : [];
    if (tags.length > 0) {
      const set = tagCollector.get(userId) ?? new Set<string>();
      for (const t of tags) {
        if (typeof t === 'string' && t.trim()) set.add(t.trim());
      }
      tagCollector.set(userId, set);
    }
  }

  const userIds = Array.from(itemsByUser.keys());
  const profiles = await Promise.all(
    userIds.map((uid) => getDoc(doc(db, 'users', uid)))
  );

  const contributions: PolishContribution[] = [];
  const tagsUnionSet = new Set<string>();
  profiles.forEach((snap, i) => {
    if (!snap.exists()) return;
    const profile = snap.data();
    if (profile.privacy?.showCollection === false) return;

    // Union in this user's tags — private users get filtered above.
    const uid = userIds[i];
    const userTags = tagCollector.get(uid);
    if (userTags) for (const t of userTags) tagsUnionSet.add(t);

    const photos: ContributorPhoto[] = [];
    for (const item of itemsByUser.get(uid) ?? []) {
      photos.push(...extractPhotos(item));
    }
    if (photos.length === 0) return;

    contributions.push({
      userId: uid,
      username: profile.username,
      displayName: profile.displayName,
      avatarUrl: profile.avatarUrl,
      photos,
    });
  });

  // Sort alphabetically so tag chip order is stable across page loads.
  const tagsUnion = Array.from(tagsUnionSet).sort((a, b) =>
    a.localeCompare(b)
  );
  return { contributions, tagsUnion };
}

export async function getOfficialBrandProduct(
  brandId: string,
  productId: string
): Promise<{ id: string; data: Record<string, any> } | null> {
  const snap = await getDoc(doc(db, 'brands', brandId, 'items', productId));
  if (!snap.exists()) return null;
  return { id: snap.id, data: snap.data() };
}

/**
 * Find an official brand item by brand slug + name slug. Used by
 * non-polish detail pages, which don't have a polishCatalog entry with
 * verifiedBrandId/officialProductId to point at directly. Returns null
 * if the brand isn't verified or the item hasn't been added to the
 * brand's catalog.
 *
 * Queries by the composite `slug` field (`${brandSlug}__${nameSlug}`)
 * which the `onBrandProductWrite` trigger stamps onto every brand item.
 * BrandProductModal doesn't write nameSlug/brandSlug directly, so those
 * fields aren't reliably present on brand items — the composite slug is.
 */
export async function findOfficialBrandItemBySlug(
  brandSlug: string,
  nameSlug: string,
  itemType: string
): Promise<{ id: string; brandId: string; data: Record<string, any> } | null> {
  const brandsSnap = await getDocs(
    query(collection(db, 'brands'), where('slug', '==', brandSlug), limit(1))
  );
  if (brandsSnap.empty) return null;
  const brandDoc = brandsSnap.docs[0];
  const compositeSlug = `${brandSlug}__${nameSlug}`;
  // Try slug first (populated by trigger on every brand item write).
  const bySlug = await getDocs(
    query(
      collection(db, 'brands', brandDoc.id, 'items'),
      where('slug', '==', compositeSlug),
      limit(4)
    )
  );
  const matches = bySlug.docs;
  if (matches.length === 0) return null;
  // Filter by itemType in memory so a brand with both a "Sunset" polish
  // and a "Sunset" powder returns the right one for this URL. If none
  // of the matches carry an itemType (legacy items), fall back to the
  // first — better than returning null.
  const typeMatch = matches.find((d) => (d.data() as any).itemType === itemType);
  const winner = typeMatch ?? matches[0];
  return { id: winner.id, brandId: brandDoc.id, data: winner.data() };
}

export { polishSlug };